import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, Upload, Sparkle, Trash, FileText, PlayCircle, UsersThree, ChartLine, CloudArrowUp, Download, MagnifyingGlass
} from "@phosphor-icons/react";

export default function TrainingDetail() {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { isTrainingAdmin } = useAuth();

  const [training, setTraining] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [results, setResults] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Generate state
  const [genOpen, setGenOpen] = useState(false);
  const [genCfg, setGenCfg] = useState({ num_mcq: 5, num_true_false: 3, num_short: 2, difficulty: "medium" });
  const [generating, setGenerating] = useState(false);

  // Assign state
  const [assignOpen, setAssignOpen] = useState(false);
  const [pickedUsers, setPickedUsers] = useState({});

  // Search state for the three employee lists in this page
  const [assignmentsSearch, setAssignmentsSearch] = useState("");
  const [pickerSearch, setPickerSearch] = useState("");
  const [resultsSearch, setResultsSearch] = useState("");

  const filteredAssignments = useMemo(() => {
    const q = assignmentsSearch.trim().toLowerCase();
    if (!q) return assignments;
    return assignments.filter((a) =>
      (a.user?.name || "").toLowerCase().includes(q) ||
      (a.user?.email || "").toLowerCase().includes(q) ||
      (a.user?.department || "").toLowerCase().includes(q)
    );
  }, [assignments, assignmentsSearch]);

  const filteredPickerUsers = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.department || "").toLowerCase().includes(q)
    );
  }, [users, pickerSearch]);

  const filteredResultRows = useMemo(() => {
    if (!results?.rows) return [];
    const q = resultsSearch.trim().toLowerCase();
    if (!q) return results.rows;
    return results.rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.department || "").toLowerCase().includes(q)
    );
  }, [results, resultsSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [
        axios.get(`${API}/training/${trainingId}`, { withCredentials: true }),
        axios.get(`${API}/training/${trainingId}/materials`, { withCredentials: true }),
        axios.get(`${API}/training/${trainingId}/questions`, { withCredentials: true }),
        axios.get(`${API}/training/${trainingId}/assignments`, { withCredentials: true }),
      ];
      if (isTrainingAdmin) {
        calls.push(axios.get(`${API}/training/${trainingId}/results`, { withCredentials: true }));
        calls.push(axios.get(`${API}/users`, { withCredentials: true }));
      }
      const res = await Promise.all(calls);
      setTraining(res[0].data);
      setMaterials(res[1].data);
      setQuestions(res[2].data);
      setAssignments(res[3].data);
      if (isTrainingAdmin) {
        setResults(res[4].data);
        setUsers(res[5].data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load training");
    } finally {
      setLoading(false);
    }
  }, [trainingId, isTrainingAdmin]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file) => {
    if (!file) return;
    const lname = (file.name || "").toLowerCase();
    if (!/\.(pdf|ppt|pptx)$/i.test(lname)) {
      toast.error("Only PDF or PowerPoint (.ppt, .pptx) files are allowed");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/training/${trainingId}/materials/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const deleteMaterial = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    await axios.delete(`${API}/materials/${id}`, { withCredentials: true });
    toast.success("Deleted");
    load();
  };

  const generateQuestions = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/training/${trainingId}/generate-questions`, genCfg, { withCredentials: true });
      toast.success(`Generated ${res.data.generated} questions`);
      setGenOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const updateQuestion = async (q, patch) => {
    try {
      await axios.patch(`${API}/questions/${q.question_id}`, patch, { withCredentials: true });
      load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await axios.delete(`${API}/questions/${id}`, { withCredentials: true });
    load();
  };

  const assignUsers = async () => {
    const ids = Object.keys(pickedUsers).filter((k) => pickedUsers[k]);
    if (ids.length === 0) {
      toast.error("Select at least one user");
      return;
    }
    try {
      const res = await axios.post(`${API}/training/${trainingId}/assign`, { user_ids: ids }, { withCredentials: true });
      toast.success(`Assigned ${res.data.created} • Skipped ${res.data.skipped}`);
      setAssignOpen(false);
      setPickedUsers({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    }
  };

  const removeAssignment = async (id) => {
    if (!window.confirm("Remove this assignment?")) return;
    await axios.delete(`${API}/assignments/${id}`, { withCredentials: true });
    load();
  };

  const exportCsv = () => {
    window.open(`${API}/training/${trainingId}/results.csv`, "_blank");
  };

  const syncZoho = async () => {
    if (!window.confirm("Push completed results to Zoho People? (Mocked sync — logs locally)")) return;
    try {
      const res = await axios.post(`${API}/training/${trainingId}/sync-zoho`, {}, { withCredentials: true });
      toast.success(`Mock-synced ${res.data.record_count} record(s) to Zoho`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
  };

  const deleteTraining = async () => {
    if (!window.confirm("Delete this entire training (materials, questions, assignments, results)?")) return;
    try {
      await axios.delete(`${API}/training/${trainingId}`, { withCredentials: true });
      toast.success("Training deleted");
      navigate("/training");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (loading || !training) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const embedUrl = (() => {
    const url = training.recording_url || "";
    if (!url) return "";
    if (url.includes("youtube.com/watch?v=")) {
      const id = new URL(url).searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : url;
    }
    if (url.includes("youtu.be/")) {
      const id = url.split("youtu.be/")[1].split(/[?&]/)[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("drive.google.com/file/d/")) {
      const id = url.split("/file/d/")[1].split("/")[0];
      return `https://drive.google.com/file/d/${id}/preview`;
    }
    return url;
  })();

  return (
    <div className="min-h-screen bg-background" data-testid="training-detail">
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/training")} data-testid="back-to-trainings">
              <ArrowLeft size={18} className="mr-2" />Back
            </Button>
            <div>
              <h1 className="text-lg font-bold">{training.title}</h1>
              <p className="text-xs text-muted-foreground">{training.trainer_name || "—"} • {training.department || "—"}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge variant={training.status === "published" ? "default" : "secondary"}>{training.status}</Badge>
            {isTrainingAdmin && (
              <Button variant="ghost" size="sm" onClick={deleteTraining} className="text-destructive" data-testid="delete-training-btn">
                <Trash size={16} />
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-secondary/50 flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            {isTrainingAdmin && <TabsTrigger value="materials" data-testid="tab-materials"><FileText size={14} className="mr-1" /> Materials</TabsTrigger>}
            {isTrainingAdmin && <TabsTrigger value="questions" data-testid="tab-questions"><Sparkle size={14} className="mr-1" /> Questions ({questions.length})</TabsTrigger>}
            {isTrainingAdmin && <TabsTrigger value="assignees" data-testid="tab-assignees"><UsersThree size={14} className="mr-1" /> Assignees ({assignments.length})</TabsTrigger>}
            {isTrainingAdmin && <TabsTrigger value="results" data-testid="tab-results"><ChartLine size={14} className="mr-1" /> Results</TabsTrigger>}
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Training Information</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Trainer:</span> {training.trainer_name || "—"}</div>
                <div><span className="text-muted-foreground">Department:</span> {training.department || "—"}</div>
                <div><span className="text-muted-foreground">Training date:</span> {training.training_date || "—"}</div>
                <div><span className="text-muted-foreground">Due:</span> {training.due_date || "—"}</div>
                <div><span className="text-muted-foreground">Passing score:</span> {training.passing_score}%</div>
                <div><span className="text-muted-foreground">Certificate validity:</span> {training.certificate_validity_months} months</div>
                <div><span className="text-muted-foreground">Retakes:</span> {training.allow_retakes ? "Allowed" : "Not allowed"}</div>
                <div><span className="text-muted-foreground">Questions per attempt:</span> {training.questions_per_attempt > 0 ? training.questions_per_attempt : "All available"}</div>
                <div><span className="text-muted-foreground">Min. watch %:</span> {training.min_watch_percent > 0 ? `${training.min_watch_percent}%` : "No gate"}</div>
              </CardContent>
            </Card>

            {training.description && (
              <Card><CardHeader><CardTitle>Description</CardTitle></CardHeader><CardContent className="text-sm whitespace-pre-wrap">{training.description}</CardContent></Card>
            )}

            {embedUrl && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><PlayCircle size={20} /> Recording</CardTitle></CardHeader>
                <CardContent>
                  <div className="aspect-video">
                    <iframe src={embedUrl} className="w-full h-full rounded-md border" allow="autoplay; encrypted-media" allowFullScreen title="Training recording" />
                  </div>
                  <a href={training.recording_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-2 inline-block">Open in new tab</a>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Materials */}
          {isTrainingAdmin && (
            <TabsContent value="materials">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Training Materials</h2>
                <div className="flex gap-2">
                  <label>
                    <input type="file" accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation" hidden onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} data-testid="material-file-input" />
                    <Button variant="default" asChild>
                      <span className="cursor-pointer"><Upload size={16} className="mr-2" />Upload PDF / PPT</span>
                    </Button>
                  </label>
                </div>
              </div>
              <Card><CardContent className="pt-4">
                {materials.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No materials yet. Upload a PDF or PPT to enable AI question generation.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>File</TableHead><TableHead>Type</TableHead><TableHead>Size</TableHead><TableHead>Uploaded</TableHead><TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {materials.map((m) => (
                        <TableRow key={m.material_id}>
                          <TableCell className="font-medium">{m.file_name}</TableCell>
                          <TableCell><Badge variant="secondary">{m.file_type}</Badge></TableCell>
                          <TableCell>{Math.round((m.size_bytes || 0) / 1024)} KB</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{m.created_at?.split("T")[0]}</TableCell>
                          <TableCell><Button size="sm" variant="ghost" onClick={() => deleteMaterial(m.material_id)}><Trash size={14} className="text-destructive" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent></Card>
            </TabsContent>
          )}

          {/* Questions */}
          {isTrainingAdmin && (
            <TabsContent value="questions">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Assessment Questions</h2>
                <Button onClick={() => setGenOpen(true)} data-testid="generate-questions-btn">
                  <Sparkle size={16} className="mr-2" />Generate with AI
                </Button>
              </div>
              {questions.length === 0 ? (
                <Card><CardContent className="py-8 text-center text-muted-foreground">No questions yet. Upload material then click "Generate with AI".</CardContent></Card>
              ) : (
                <div className="space-y-3">
                  {questions.map((q, idx) => (
                    <Card key={q.question_id} data-testid={`question-card-${q.question_id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">{q.question_type.replace("_", " ")}</Badge>
                              <Badge variant="secondary" className="text-xs">{q.difficulty}</Badge>
                              {q.topic && <span className="text-xs text-muted-foreground">{q.topic}</span>}
                            </div>
                            <Textarea
                              value={q.question_text}
                              onChange={(e) => setQuestions(questions.map((x) => x.question_id === q.question_id ? { ...x, question_text: e.target.value } : x))}
                              onBlur={(e) => updateQuestion(q, { question_text: e.target.value })}
                              rows={2}
                              className="font-medium text-sm"
                              data-testid={`question-text-${idx}`}
                            />
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => deleteQuestion(q.question_id)}>
                            <Trash size={14} className="text-destructive" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="text-sm space-y-2">
                        {q.question_type === "mcq" && (
                          <div className="space-y-1">
                            {(q.options || []).map((opt, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <Checkbox checked={(q.correct_answer || "") === opt} onCheckedChange={() => updateQuestion(q, { correct_answer: opt })} />
                                <Input
                                  value={opt}
                                  onChange={(e) => {
                                    const ops = [...q.options];
                                    ops[i] = e.target.value;
                                    setQuestions(questions.map((x) => x.question_id === q.question_id ? { ...x, options: ops } : x));
                                  }}
                                  onBlur={() => updateQuestion(q, { options: q.options, correct_answer: q.correct_answer })}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {q.question_type === "true_false" && (
                          <div className="flex gap-3">
                            {["True", "False"].map((opt) => (
                              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" checked={(q.correct_answer || "") === opt} onChange={() => updateQuestion(q, { correct_answer: opt })} />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {q.question_type === "short_answer" && (
                          <div>
                            <Label className="text-xs">Expected answer (used by AI to grade)</Label>
                            <Textarea
                              value={q.expected_answer || ""}
                              onChange={(e) => setQuestions(questions.map((x) => x.question_id === q.question_id ? { ...x, expected_answer: e.target.value } : x))}
                              onBlur={(e) => updateQuestion(q, { expected_answer: e.target.value })}
                              rows={2}
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Assignees */}
          {isTrainingAdmin && (
            <TabsContent value="assignees">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Assigned Employees</h2>
                <Button onClick={() => setAssignOpen(true)} data-testid="assign-users-btn">
                  <UsersThree size={16} className="mr-2" />Assign Employees
                </Button>
              </div>
              <Card><CardContent className="pt-4">
                {assignments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No one assigned yet.</p>
                ) : (
                  <>
                    <div className="relative mb-3">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={assignmentsSearch}
                        onChange={(e) => setAssignmentsSearch(e.target.value)}
                        placeholder={`Search ${assignments.length} assigned employees by name, email or department...`}
                        className="pl-9"
                        data-testid="assignments-search"
                      />
                    </div>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Dept</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead><TableHead></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {filteredAssignments.length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4">No matches for "{assignmentsSearch}"</TableCell></TableRow>
                        ) : filteredAssignments.map((a) => (
                          <TableRow key={a.assignment_id}>
                            <TableCell className="font-medium">{a.user?.name}</TableCell>
                            <TableCell className="text-sm">{a.user?.email}</TableCell>
                            <TableCell><Badge variant="outline">{a.user?.department || "—"}</Badge></TableCell>
                            <TableCell><Badge variant={a.status === "passed" ? "default" : a.status === "failed" ? "destructive" : "secondary"}>{a.status}</Badge></TableCell>
                            <TableCell>{a.last_score != null ? `${a.last_score}%` : "—"}</TableCell>
                            <TableCell><Button size="sm" variant="ghost" onClick={() => removeAssignment(a.assignment_id)}><Trash size={14} className="text-destructive" /></Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent></Card>
            </TabsContent>
          )}

          {/* Results */}
          {isTrainingAdmin && (
            <TabsContent value="results">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Results</h2>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download size={16} className="mr-2" />Export CSV</Button>
                  <Button onClick={syncZoho} data-testid="sync-zoho-btn"><CloudArrowUp size={16} className="mr-2" />Sync to Zoho</Button>
                </div>
              </div>
              {results && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <Card><CardContent className="pt-4"><p className="text-3xl font-bold">{results.summary.total_assigned}</p><p className="text-xs text-muted-foreground">Assigned</p></CardContent></Card>
                    <Card><CardContent className="pt-4"><p className="text-3xl font-bold text-green-600">{results.summary.passed}</p><p className="text-xs text-muted-foreground">Passed</p></CardContent></Card>
                    <Card><CardContent className="pt-4"><p className="text-3xl font-bold text-red-600">{results.summary.failed}</p><p className="text-xs text-muted-foreground">Failed</p></CardContent></Card>
                    <Card><CardContent className="pt-4"><p className="text-3xl font-bold text-amber-600">{results.summary.pending}</p><p className="text-xs text-muted-foreground">Pending</p></CardContent></Card>
                    <Card><CardContent className="pt-4"><p className="text-3xl font-bold">{results.summary.pass_rate}%</p><p className="text-xs text-muted-foreground">Pass Rate</p></CardContent></Card>
                  </div>
                  <Card><CardContent className="pt-4">
                    <div className="relative mb-3">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={resultsSearch}
                        onChange={(e) => setResultsSearch(e.target.value)}
                        placeholder={`Search ${results.rows?.length || 0} employees by name, email or department...`}
                        className="pl-9"
                        data-testid="results-search"
                      />
                    </div>
                    <Table>
                      <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Dept</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead><TableHead>Completed</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {filteredResultRows.length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">No matches for "{resultsSearch}"</TableCell></TableRow>
                        ) : filteredResultRows.map((r) => (
                          <TableRow key={r.user_id}>
                            <TableCell className="font-medium">{r.name}<div className="text-xs text-muted-foreground">{r.email}</div></TableCell>
                            <TableCell>{r.department || "—"}</TableCell>
                            <TableCell>
                              <Badge variant={r.result === "passed" ? "default" : r.result === "failed" ? "destructive" : "secondary"}>
                                {r.result || r.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{r.score != null ? `${r.score}%` : "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{r.completed_at?.split("T")[0] || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent></Card>
                </>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate Questions with AI</DialogTitle>
            <DialogDescription>GPT-5.2 will read your uploaded PDF / PPT materials to draft a quiz.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>MCQ count</Label><Input type="number" min={0} max={20} value={genCfg.num_mcq} onChange={(e) => setGenCfg({ ...genCfg, num_mcq: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>True/False count</Label><Input type="number" min={0} max={20} value={genCfg.num_true_false} onChange={(e) => setGenCfg({ ...genCfg, num_true_false: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>Short Answer count</Label><Input type="number" min={0} max={10} value={genCfg.num_short} onChange={(e) => setGenCfg({ ...genCfg, num_short: parseInt(e.target.value) || 0 })} /></div>
            <div>
              <Label>Difficulty</Label>
              <Select value={genCfg.difficulty} onValueChange={(v) => setGenCfg({ ...genCfg, difficulty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={generateQuestions} disabled={generating} data-testid="confirm-generate-btn">
              {generating ? "Generating..." : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={(v) => { setAssignOpen(v); if (!v) setPickerSearch(""); }}>
        <DialogContent className="sm:max-w-lg" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign Employees</DialogTitle>
            <DialogDescription>Select users to assign this training to.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder={`Search ${users.length} employees by name, email or department...`}
              className="pl-9"
              data-testid="assign-search"
              autoFocus
            />
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1 mt-2">
            {filteredPickerUsers.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">No employees match "{pickerSearch}"</p>
            ) : filteredPickerUsers.map((u) => {
              const alreadyAssigned = assignments.some((a) => a.user_id === u.user_id);
              return (
                <label key={u.user_id} className={`flex items-center gap-3 p-2 rounded hover:bg-secondary cursor-pointer ${alreadyAssigned ? "opacity-50" : ""}`}>
                  <Checkbox
                    checked={!!pickedUsers[u.user_id]}
                    disabled={alreadyAssigned}
                    onCheckedChange={(v) => setPickedUsers({ ...pickedUsers, [u.user_id]: v })}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.email} • {u.department || "—"}</div>
                  </div>
                  {alreadyAssigned && <Badge variant="secondary" className="text-xs">Assigned</Badge>}
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={assignUsers} data-testid="confirm-assign-btn">Assign Selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
